import { useCallback } from 'react';
import { IRelation } from './RelationForm';
import { useWatch } from 'antd/es/form/Form';
import { ADMIN_ROUTES } from 'constants/routes';
import { useNavigate } from 'react-router-dom';
import { TypeOfEntityType } from 'models/ComunicationHub';

export const useRelation = (props: IRelation) => {
  const { form, relationList, onSave, onRemove, onEdit } = props;
  const navigate = useNavigate();
  const relationType = useWatch('relationType', form);
  const relationEntity = useWatch('relationEntity', form);

  const onRelationCancel = useCallback(() => {
    form.resetFields(['relationType']);
    form.resetFields(['relationEntity']);
  }, [form]);

  const onRelationSave = useCallback(async () => {
    try {
      await form.validateFields(['relationType', 'relationEntity']);
      const { relationType, relationEntity } = form.getFieldsValue(['relationType', 'relationEntity']);
      onSave([
        ...relationList,
        {
          relationType,
          relationEntity,
        },
      ]);
      form.resetFields(['relationType', 'relationEntity']);
    } catch {
      //handle
    }
  }, [form, relationList, onSave]);

  const onEditRelationSave = useCallback(
    async (id: string, type: string) => {
      try {
        await form.validateFields(['editRelationType', 'editRelationEntity']);
        const { editRelationType, editRelationEntity } = form.getFieldsValue([
          'editRelationType',
          'editRelationEntity',
        ]);
        onSave(
          relationList.map((relation) => {
            if (relation.relationEntity === id && relation.relationType === type) {
              return {
                ...relation,
                relationType: editRelationType,
                relationEntity: editRelationEntity,
              };
            } else {
              return relation;
            }
          })
        );
        form.resetFields(['editRelationType', 'editRelationEntity']);
      } catch {
        // handle catch
      }
    },
    [form, onSave, relationList]
  );

  const onRelationEdit = useCallback(
    (id: string, type: string) => {
      onEdit && onEdit(id, type);
      const relationItem = relationList.find(
        (relation) => relation.relationEntity === id && relation.relationType === type
      );
      if (relationItem) {
        form.setFieldsValue({
          editRelationType: relationItem.relationType,
          editRelationEntity: relationItem.relationEntity,
        });
      }
    },
    [relationList, form, onEdit]
  );

  const onRelationRemove = useCallback(
    (id: string, type: string) => {
      onRemove &&
        onRemove(
          relationList.filter((relation: any) => relation.relationEntity !== id || relation.relationType !== type)
        );
    },
    [relationList, onRemove]
  );

  const onRouteChange = useCallback(
    (entityName: TypeOfEntityType, id: string) => {
      switch (entityName) {
        case 'SUPPLIER':
          navigate(`${ADMIN_ROUTES.suppliers.view}/${id}/general`);
          return;
        case 'BRAND':
          navigate(`${ADMIN_ROUTES.brand.view}/${id}/management`);
          return;
        case 'CLIENT':
          navigate(`${ADMIN_ROUTES.client}/${id}/general`);
          return;
        case 'LEAD':
          navigate(`${ADMIN_ROUTES.leads.view}/${id}/general`);
          return;
        case 'DEAL':
          navigate(`${ADMIN_ROUTES.deals.view}/${id}/purchase-request`);
          return;
        default:
          return;
      }
    },
    [navigate]
  );

  return {
    onRelationCancel,
    onRelationSave,
    onEditRelationSave,
    onRelationRemove,
    onRelationEdit,
    isChanged: !!relationType || !!relationEntity,
    onRouteChange,
  };
};
